import React, { createContext, useState, useEffect } from "react";
import Box from "@mui/material/Box";
import apiService from "../services/api.service";
import CircularIndeterminate from "../components/Loader.component";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState(null); // Initialize as null instead of possibly invalid JSON

  // Cross-app handoff: another OPUS app (e.g. AeDrive) can redirect here with
  // ?h=<handoff JWT> so the visitor lands already authenticated instead of
  // being bounced to /login. Exchanges for a real Dashboard session, same
  // shape as a normal password login.
  const tryHandoffExchange = async () => {
    const params = new URLSearchParams(window.location.search);
    const handoff = params.get("h");
    if (!handoff) return false;

    params.delete("h");
    const cleanUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : "");

    try {
      const res = await apiService.exchangeHandoff(handoff);
      const d = res?.data;
      if (!d?.accessToken) {
        window.history.replaceState({}, "", cleanUrl);
        return false;
      }

      // The handoff response is a flat summary (role as a bare string, orgId
      // as a plain string) — too thin for PermissionsContext (needs
      // role.permissions[]) and org-display code (needs the nested
      // organization_id object). Set tokens first so this call is
      // authenticated, then fetch the same full user record a normal
      // password login would have stored.
      localStorage.setItem("accessToken", d.accessToken);
      if (d.refreshToken) localStorage.setItem("refreshToken", d.refreshToken);
      localStorage.setItem("isAuthenticated", "true");

      const username = d.user_name || d.userName || d.username;
      try {
        const fullUser = await apiService.getUserData(username);
        localStorage.setItem("userData", JSON.stringify(fullUser));
      } catch (fetchErr) {
        console.error("Failed to fetch full user record after handoff — falling back to thin payload:", fetchErr);
        const { accessToken, refreshToken, ...userData } = d;
        localStorage.setItem("userData", JSON.stringify({ data: userData }));
      }

      window.history.replaceState({}, "", cleanUrl);
      return true;
    } catch (error) {
      console.error("Handoff exchange failed:", error);
      window.history.replaceState({}, "", cleanUrl);
      return false;
    }
  };

  useEffect(() => {
    const init = async () => {
      const handoffApplied = await tryHandoffExchange();

      const storedUser = localStorage.getItem("isAuthenticated");
      const userInfo = localStorage.getItem("userData");
      if ((handoffApplied || storedUser === "true") && userInfo) {
        try {
          setUser(true);
          setUserInfo(JSON.parse(userInfo));
        } catch (error) {
          console.error("Error parsing user data:", error);
          localStorage.removeItem("userData"); // Clear invalid data
        }
      }
      setLoading(false);
    };
    init();
  }, []);

  const login = () => {
    localStorage.setItem("isAuthenticated", "true");
    setUser(true);
        const userInfoData = localStorage.getItem("userData");
    if (userInfoData) {
      try {
        setUserInfo(JSON.parse(userInfoData));
      } catch (error) {
        console.error("Error parsing user data during login:", error);
      }
    }
  };

  const logout = () => {
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("userData");
    setUser(null);
    setUserInfo(null);
  };

  // OPUS-913: patch the signed-in user's profile fields in context + persisted
  // userData so edits (name/email/avatar) reflect immediately without a reload.
  const updateUserInfo = (patch) => {
    setUserInfo((prev) => {
      if (!prev) return prev;
      const next = { ...prev, data: { ...(prev.data || {}), ...patch } };
      try { localStorage.setItem("userData", JSON.stringify(next)); } catch (_) { /* ignore */ }
      return next;
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularIndeterminate size={60} />
      </Box>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, userInfo, updateUserInfo }}>
      {children}
    </AuthContext.Provider>
  );
};